// =====================================================================
// TRADE CONFIG — Hybrid Option C, runtime-switchable
// ---------------------------------------------------------------------
// `TRADE_MODE` is read from localStorage at runtime so admins can
// switch trades from /admin/trade-mode without a code edit.
// Falls back to "all" (multi-trade demo).
// =====================================================================

import { useEffect, useState } from "react";
import { TRADES, type TradePage } from "@/data/servicesData";

export type TradeMode = "all" | TradePage["slug"];

const STORAGE_KEY = "lovable.tradeMode";
const EVENT_NAME = "trade-mode-change";

const readMode = (): TradeMode => {
  if (typeof window === "undefined") return "all";
  const v = window.localStorage.getItem(STORAGE_KEY) as TradeMode | null;
  return v && (v === "all" || TRADES.some((t) => t.slug === v)) ? v : "all";
};

export const getTradeMode = (): TradeMode => readMode();

export const setTradeMode = (mode: TradeMode): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, mode);
  window.dispatchEvent(new Event(EVENT_NAME));
};

export const isSingleTrade = (): boolean => readMode() !== "all";

export const getActiveTrades = (): TradePage[] => {
  const mode = readMode();
  if (mode === "all") return TRADES;
  return TRADES.filter((t) => t.slug === mode);
};

export const getPrimaryTrade = (): TradePage | null => {
  const mode = readMode();
  if (mode === "all") return null;
  return TRADES.find((t) => t.slug === mode) ?? null;
};

// React hook so components re-render when admin changes the mode.
export const useTradeMode = (): TradeMode => {
  const [mode, setMode] = useState<TradeMode>(readMode());
  useEffect(() => {
    const handler = () => setMode(readMode());
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return mode;
};

// Per-trade marketing copy that swaps onto the homepage hero
// when running in single-trade mode.
export const BRAND_COPY: Record<
  Exclude<TradeMode, "all">,
  { eyebrow: string; headline: string; sub: string }
> = {
  hvac: {
    eyebrow: "Local HVAC done right",
    headline: "Heating & cooling, fixed today.",
    sub: "Same-day AC, furnace, and indoor-air-quality service from licensed techs. Upfront flat-rate pricing, no surprises.",
  },
  plumbing: {
    eyebrow: "24/7 licensed plumbers",
    headline: "Leaks, drains, water heaters — fixed right.",
    sub: "From clogged drains to full repipes, we show up fast and quote upfront. Master plumbers on every job.",
  },
  electrical: {
    eyebrow: "Licensed master electricians",
    headline: "Safe, code-compliant electrical service.",
    sub: "Panel upgrades, EV chargers, lighting, and emergency repairs — always permitted, always inspected.",
  },
  roofing: {
    eyebrow: "Local roofing contractor",
    headline: "Repair, replacement, and storm response.",
    sub: "Free drone inspection. Insurance-claim experts. Lifetime workmanship warranty available.",
  },
  handyman: {
    eyebrow: "One call for the whole list",
    headline: "Skilled handyman service for every home.",
    sub: "Drywall, mounting, doors, carpentry. Flat-rate packages by the half-day or full day.",
  },
  landscaping: {
    eyebrow: "Curb appeal that lasts",
    headline: "Lawn care, irrigation, and outdoor living.",
    sub: "Weekly maintenance, hardscape, and seasonal cleanups from a crew that shows up the same day, every visit.",
  },
};
